import React from 'react';
import {
  ArrowUpRight,
  Fuel,
  IndianRupee,
  TrendingUp,
  Truck,
  Users,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

function BentoTile({ className, variant = 'panel', children, featured }) {
  return (
    <div
      className={cn(
        'de-ultra-card group transition-all duration-300 hover:-translate-y-1',
        featured && 'lg:col-span-2 lg:row-span-2',
        variant === 'warn' && 'border-de-accent/40',
        className,
      )}
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-de-gold/10 blur-3xl transition group-hover:bg-de-gold/20" />
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-de-gold/40 to-transparent" />
      <div className="relative p-4 sm:p-6">{children}</div>
    </div>
  );
}

function StatTile({ icon: Icon, value, label, sub, warn }) {
  return (
    <BentoTile variant={warn ? 'warn' : 'panel'}>
      <div className="de-stat-ring mb-4">
        <Icon className={cn('h-6 w-6', warn ? 'text-de-accent' : 'text-de-gold')} />
      </div>
      <p className="text-3xl font-black tracking-tight text-white sm:text-4xl">{value}</p>
      <p className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-de-muted">{label}</p>
      {sub ? <p className={cn('mt-2 text-xs font-medium', warn ? 'text-de-accent' : 'text-de-gold')}>{sub}</p> : null}
    </BentoTile>
  );
}

export default function BentoOverview({
  stats,
  totalRevenue,
  activeBookings,
  lowFuelMachines,
  machineData,
  alerts,
  onNavigate,
}) {
  const { t } = useLanguage();
  const revenueDisplay = totalRevenue > 0 ? `₹${(totalRevenue / 100000).toFixed(1)}L` : '₹0';
  const activeMachines = machineData.filter((m) => m.status === 'Active').length;
  const hasFuelAlert = lowFuelMachines.length > 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2 lg:gap-5">
        <BentoTile featured variant="gold">
          <div className="flex min-h-[220px] flex-col justify-between">
            <div>
              <div className="mb-4 flex items-start justify-between">
                <Badge className="border-de-gold/50 bg-de-gold/20 text-de-gold shadow-md">{t('realtimeRevenue')}</Badge>
                <div className="de-stat-ring">
                  <IndianRupee className="h-7 w-7 text-de-gold" />
                </div>
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-de-muted">{t('monthlyBilled')}</p>
              <p className="mt-3 de-shimmer-text text-5xl font-black tracking-tight sm:text-6xl">{revenueDisplay}</p>
              <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-sm font-semibold text-emerald-400">
                <TrendingUp className="h-4 w-4" />
                {t('growthVsLastMonth')}
              </p>
              <p className="mt-2 text-xs text-de-muted">{t('karadOpsHub')}</p>
            </div>
            <Button
              type="button"
              variant="outline"
              className="mt-6 w-fit border-de-gold/50 bg-de-gold/10 px-5 py-2.5 font-bold text-de-gold hover:bg-de-gold/20"
              onClick={() => onNavigate('reports')}
            >
              {t('viewFullReports')}
              <ArrowUpRight className="h-4 w-4" />
            </Button>
          </div>
        </BentoTile>

        <StatTile icon={Truck} value={machineData.length} label={t('totalMachinesLabel')} sub={t('activeNowSuffix').replace('{{n}}', activeMachines)} />
        <StatTile icon={Users} value={activeBookings.length || stats?.[4]?.value || '0'} label={t('liveBookings')} sub={t('livePipeline')} />
        <StatTile icon={TrendingUp} value={stats?.[3]?.value || '—'} label={t('commissionLabel')} sub={stats?.[3]?.change || t('yourEarnings')} />
        <StatTile
          icon={Fuel}
          value={lowFuelMachines.length}
          label={t('lowFuelAlert')}
          sub={hasFuelAlert ? t('immediateActionRequired') : t('fleetHealthy')}
          warn={hasFuelAlert}
        />
      </div>

      <div className="de-ultra-card">
        <div className="relative p-4 sm:p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="m-0 text-sm font-bold uppercase tracking-[0.2em] text-de-gold">{t('liveSignalFeed')}</h3>
              <p className="m-0 mt-1 text-xs text-de-muted">{t('criticalAlertsHealth')}</p>
            </div>
            <Badge variant="success" className="px-3 py-1">{t('streamingLabel')}</Badge>
          </div>
          <div className="max-h-[240px] space-y-2 overflow-y-auto pr-1">
            {alerts.slice(0, 10).map((a, i) => (
              <div
                key={i}
                className="group flex items-start gap-3 rounded-xl border border-de-gold/10 bg-gradient-to-r from-black/40 to-transparent px-4 py-3 transition hover:border-de-gold/25 hover:bg-de-gold/5"
                style={{ borderLeftWidth: 3, borderLeftColor: a.color }}
              >
                <span className="mt-0.5 text-xl">{a.icon}</span>
                <div className="min-w-0 flex-1">
                  <p className="m-0 text-sm font-medium text-slate-100 group-hover:text-white">{a.msg}</p>
                  <p className="m-0 mt-1 text-[10px] uppercase tracking-wider text-de-muted">{a.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
